import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';

const PythonRegex = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Pattern Matching</div>
                <h1>Intro to Regular Expressions</h1>
                <p className="hero-lead">
                    Regular Expressions (Regex) are a language within a language.
                    They allow you to find needles in haystacks, validate emails, and scrape data with surgical precision.
                </p>
            </div>

            <div className="content-section chapter-intro">
                <p>
                    A Regular Expression is a special sequence of characters that helps you match or find other strings or sets of strings.
                    It is heavily based on mathematical Computer Science theory (Finite State Machines), but in practice,
                    it's a superpower for text processing.
                </p>
                <p>
                    In Python, we use the standard library module <code>re</code>.
                </p>

                <div className="learning-objectives">
                    <h3>What You'll Learn</h3>
                    <ul>
                        <li><strong>Raw Strings:</strong> Why <code>r"..."</code> is mandatory for regex.</li>
                        <li><strong>The API:</strong> <code>match</code> vs <code>search</code> vs <code>findall</code>.</li>
                        <li><strong>Quantifiers:</strong> Matching one, many, or optional characters (<code>* + ?</code>).</li>
                        <li><strong>Groups:</strong> Extracting specific data (and why Named Groups are best).</li>
                        <li><strong>Performance:</strong> Using <code>re.compile()</code> to cache patterns.</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>The <code>r""</code> Revolution</h2>
                <p>
                    Before writing a single regex, you must understand <strong>Raw Strings</strong>.
                    In standard Python strings, the backslash <code>\</code> is an escape character (e.g., <code>\n</code> is newline).
                    In Regex, <code>\</code> is <em>also</em> a special character (e.g., <code>\d</code> is a digit).
                </p>
                <p>
                    This creates "Backslash Hell" where you need <code>\\\\</code> to match a single backslash.
                    <strong>Always prefix regex strings with <code>r</code></strong> to tell Python "Ignore backslashes".
                </p>

                <div className="code-example">
                    <CodeBlock language="python" code={`import re

#  BAD: Python interprets \b as 'backspace' character
pattern = "\bword\b" 

# GOOD: Raw string passes \b literally to the regex engine
pattern = r"\bword\b"`} />
                </div>
            </div>

            <div className="content-section">
                <h2>The Cheat Sheet</h2>
                <p>
                    Regex syntax is dense. Here are the 20% of symbols you'll use 80% of the time.
                </p>
                <div className="table-container">
                    <table className="course-table">
                        <thead>
                            <tr>
                                <th style={{ width: '15%' }}>Symbol</th>
                                <th style={{ width: '85%' }}>Meaning</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>.</code></td>
                                <td>Matches <strong>any character</strong> (except newline).</td>
                            </tr>
                            <tr>
                                <td><code>^</code> / <code>$</code></td>
                                <td>Anchors: <strong>Start</strong> / <strong>End</strong> of the string.</td>
                            </tr>
                            <tr>
                                <td><code>\d</code> / <code>\w</code></td>
                                <td>Matches <strong>Digit</strong> (0-9) / <strong>Word Char</strong> (a-z, 0-9, _).</td>
                            </tr>
                            <tr>
                                <td><code>*</code></td>
                                <td>Matches <strong>0 or more</strong> repetitions.</td>
                            </tr>
                            <tr>
                                <td><code>+</code></td>
                                <td>Matches <strong>1 or more</strong> repetitions.</td>
                            </tr>
                            <tr>
                                <td><code>?</code></td>
                                <td>Matches <strong>0 or 1</strong> (Optional).</td>
                            </tr>
                            <tr>
                                <td><code>[]</code></td>
                                <td>Set: <code>[aeiou]</code> matches any vowel.</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="content-section">
                <h2>Functions: Search vs Match</h2>
                <p>
                    A common beginner bug: <code>re.match()</code> checks ONLY the start of the string.
                    <code>re.search()</code> checks ANYWHERE.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>The Subtle Difference</span>
                    </div>
                    <CodeBlock language="python" code={`import re

text = "Error: File not found"

#  re.match fails because text starts with 'Error', not 'File'
result = re.match(r"File", text)
print(result) # None

# re.search scans the whole string
result = re.search(r"File", text)
print(result) # <re.Match object; span=(7, 11), match='File'>

# re.findall returns a LIST of all matches
text = "10 apples, 20 oranges, 30 bananas"
numbers = re.findall(r"\d+", text)
print(numbers) # ['10', '20', '30']`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Advanced: Capturing Groups</h2>
                <p>
                    Often you don't just want to know <em>if</em> it matched, you want to extract specific parts.
                    We use parentheses <code>()</code> to define <strong>Groups</strong>.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Extracting Email Parts</span>
                    </div>
                    <CodeBlock language="python" code={`email = "support@example.com"

# Define groups with ()
pattern = r"(\w+)@(\w+\.\w+)"

match = re.search(pattern, email)
if match:
    # group(0) is the entire match
    print(match.group(0)) # "support@example.com"
    
    # group(1) is the first paretheses (Username)
    print(match.group(1)) # "support"
    
    # group(2) is the second parentheses (Domain)
    print(match.group(2)) # "example.com"`} />
                </div>

                <h3>Pro Tip: Named Groups</h3>
                <p>
                    Numbered groups are confusing. Use <code>(?P&lt;name&gt;...)</code> to name them!
                </p>
                <CodeBlock language="python" code={`pattern = r"(?P<user>\w+)@(?P<domain>\w+\.\w+)"
match = re.search(pattern, email)
print(match.group("user"))   # "support"`} />
            </div>

            <div className="content-section">
                <h2>Performance: Compilation</h2>
                <p>
                    If you are using a regex pattern inside a loop (e.g., checking 10,000 strings),
                    using <code>re.search()</code> directly is inefficient because Python has to re-parse the pattern string every time.
                </p>
                <p>
                    Instead, use <code>re.compile()</code> to parse the regex once into a reusable object.
                </p>

                <CodeBlock language="python" code={`# Optimized for multiple uses
email_pattern = re.compile(r"[\w\.-]+@[\w\.-]+")

users = ["a@b.com", "invalid-email", "c@d.org"]
valid_users = []

for u in users:
    if email_pattern.match(u):
        valid_users.append(u)`} />
            </div>

        </div>
    );
};

export default PythonRegex;


